import React, { useEffect, useState } from 'react';
import { FaPaperPlane, FaRegStickyNote, FaTrash } from 'react-icons/fa';
import { getVoucherStatusNotes, addVoucherStatusNote, deleteVoucherStatusNote } from '../services/voucherStatusNotesService';
import { Button } from '../shared/ui';
import ConfirmDialog from './ConfirmDialog';
import styles from './VoucherStatusNotesForm.module.css';

const NOTE_MAX = 2000;

export default function VoucherStatusNotesForm({ studentId, settlementDateId, onNotesChange, hideHeading }) {
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [noteDeleteId, setNoteDeleteId] = useState(null);
  const [noteDeletePreview, setNoteDeletePreview] = useState('');
  const [deletingNote, setDeletingNote] = useState(false);

  useEffect(() => {
    if (studentId && settlementDateId) {
      setLoading(true);
      getVoucherStatusNotes(studentId, settlementDateId)
        .then(setNotes)
        .catch(() => setNotes([]))
        .finally(() => setLoading(false));
    } else {
      setNotes([]);
    }
  }, [studentId, settlementDateId]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    const trimmed = noteText.trim();
    if (!trimmed) return;
    setError('');
    setSubmitting(true);
    try {
      const newNote = await addVoucherStatusNote({
        student_id: studentId,
        settlement_date_id: settlementDateId,
        note: trimmed
      });
      const row = {
        ...newNote,
        full_name: (window.currentUser && window.currentUser.full_name) || 'You'
      };
      const next = [row, ...notes];
      setNotes(next);
      setNoteText('');
      if (onNotesChange) onNotesChange(next.length);
    } catch (err) {
      setError('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteNote = async () => {
    if (!noteDeleteId) return;
    setError('');
    setDeletingNote(true);
    try {
      await deleteVoucherStatusNote(noteDeleteId);
      const next = notes.filter((n) => n.id !== noteDeleteId);
      setNotes(next);
      if (onNotesChange) onNotesChange(next.length);
      setNoteDeleteId(null);
      setNoteDeletePreview('');
    } catch (err) {
      setError('Failed to delete note');
    } finally {
      setDeletingNote(false);
    }
  };

  const embedded = !!hideHeading;
  const remaining = NOTE_MAX - noteText.length;

  return (
    <div
      className={[styles.root, embedded ? styles.rootEmbedded : styles.rootStandalone].filter(Boolean).join(' ')}
    >
      {!hideHeading ? <h3 className={styles.title}>Notes</h3> : null}

      <div
        className={[styles.listWrap, embedded ? styles.listWrapEmbedded : ''].filter(Boolean).join(' ')}
        aria-busy={loading}
      >
        {loading ? (
          <div className={styles.loadingState}>
            <span className={styles.spinner} aria-hidden />
            <span>Loading notes…</span>
          </div>
        ) : notes.length === 0 ? (
          <div className={styles.emptyState}>
            <FaRegStickyNote className={styles.emptyIcon} aria-hidden />
            <p className={styles.emptyTitle}>No notes yet</p>
            <p className={styles.emptyHint}>
              Add context for this settlement—follow-ups, calls, or why a row was closed.
            </p>
          </div>
        ) : (
          <div className={styles.listInner}>
            {notes.map((n, i) => (
              <article key={n.id || i} className={styles.noteCard}>
                <div className={styles.noteBody}>
                  <p className={styles.noteText}>{n.note}</p>
                  <div className={styles.noteMeta}>
                    {(n.full_name || 'User') +
                      (n.created_at ? ` · ${new Date(n.created_at).toLocaleString()}` : '')}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => {
                    const text = String(n.note || '').trim();
                    const preview =
                      text.length > 120 ? `${text.slice(0, 117).trim()}…` : text || 'this note';
                    setNoteDeletePreview(preview);
                    setNoteDeleteId(n.id);
                  }}
                  title="Remove note"
                  aria-label="Remove note"
                >
                  <FaTrash style={{ fontSize: 13 }} aria-hidden />
                </button>
              </article>
            ))}
          </div>
        )}
      </div>

      <form
        className={[styles.composer, embedded ? styles.composerEmbedded : ''].filter(Boolean).join(' ')}
        onSubmit={handleAddNote}
      >
        <label className={styles.composerLabel} htmlFor="voucher-note-input">
          New note
        </label>
        <textarea
          id="voucher-note-input"
          className={styles.textarea}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value.slice(0, NOTE_MAX))}
          placeholder="Write a note for this student and settlement period…"
          disabled={submitting || !studentId || !settlementDateId}
          rows={4}
          aria-invalid={!!error}
        />
        <div className={styles.composerActions}>
          <span className={styles.charHint} aria-live="polite">
            {remaining.toLocaleString()} characters left
          </span>
          <Button
            type="submit"
            size="sm"
            loading={submitting}
            disabled={submitting || !noteText.trim() || !studentId || !settlementDateId}
            iconLeft={<FaPaperPlane />}
          >
            Save note
          </Button>
        </div>
      </form>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <ConfirmDialog
        open={noteDeleteId != null}
        onClose={() => {
          if (!deletingNote) {
            setNoteDeleteId(null);
            setNoteDeletePreview('');
          }
        }}
        onConfirm={confirmDeleteNote}
        title="Delete this note?"
        description={
          noteDeletePreview
            ? `This note will be removed permanently. Preview: ${noteDeletePreview.replace(/"/g, "'")}`
            : 'This note will be removed permanently.'
        }
        confirmLabel="Delete note"
        cancelLabel="Keep"
        variant="danger"
        loading={deletingNote}
      />
    </div>
  );
}
